<template>
  <div class="lib__form__main" data-cy="electricity-specification-component">
    <ValidationObserver ref="validator" slim>
      <div class="lib__form__main__content">
        <template v-if="getHasEan && stateGridAccessPointInfo">
          <header class="lib__form__main__header">
            <h2 class="lib__h2">{{ $t('e-mobility.ean-result.isResultCorrect') }}</h2>
          </header>
          <resume-grid-access-point-info></resume-grid-access-point-info>
        </template>
        <template v-else-if="getHasEan && !stateGridAccessPointInfo">
          <no-grid-access-point-info @canContinue='canContinue($event)'></no-grid-access-point-info>
        </template>
        <template v-else>
          <header class="lib__form__main__header">
            <h2 class="lib__h2">{{ $t('e-mobility.electricity-installation-form.meterTypeTitle') }}
              <div class="lib__tooltips">
                <span @click="onMeterTypeModalClick">
                  <i class="fas fa-info-circle lib__clickable-tooltips"></i>
                </span>
              </div>
            </h2>
          </header>
          <electricity-installation-form v-model="electricityInstallation"></electricity-installation-form>
        </template>
      </div>
    </ValidationObserver>
    <ModalDialog :closeable="$t('common.close')" :open.sync="isMeterTypeModalOpen" size="default">
      <div>
        <p>{{ $t('e-mobility.electricity-installation-form.meterTypeModal.part1') }}</p>
        <div class="row">
          <div class="col-xs-4 col-sm-2">
            <img :src="require('@/assets/e-mobility/meter-type.png')" alt="meter-type-image">
          </div>
          <div class="col-xs-8 col-sm-10">
            <p v-html="$t('e-mobility.electricity-installation-form.meterTypeModal.part2')"></p>
            <p v-html="$t('e-mobility.electricity-installation-form.meterTypeModal.part3')"></p>
          </div>
        </div>
      </div>
    </ModalDialog>
  </div>

</template>

<script lang="ts">
import NoGridAccessPointInfo from '@/components/display/EMobility/NoGridAccessPointInfo.vue'
import ResumeGridAccessPointInfo from '@/components/display/EMobility/ResumeGridAccessPointInfo.vue'
import ElectricityInstallationForm from '@/components/inputs/EMobility/ElectricityInstallationForm/Installation.vue'
import { ConsumingDevice, ElectricityInstallation, ElectricMobilityGridAccessPointInfo } from '@/models'
import { helper } from '@/services'
import { AxiosResponse } from 'axios'
import { Component, Watch } from 'vue-property-decorator'
import { Getter, Mutation, State } from 'vuex-class'
import Step from '../Step'

@Component({
  name: 'electricity-specification-step',
  components: {
    ResumeGridAccessPointInfo,
    ElectricityInstallationForm,
    NoGridAccessPointInfo
  }
})
export default class GridAccessPointInfoOrInstallationStep extends Step {
  //#region [Data]
  @State('gridAccessPointInfo', { namespace: 'eMobility/identification' })
  public stateGridAccessPointInfo!: ElectricMobilityGridAccessPointInfo | null
  @State('electricityInstallation', { namespace: 'eMobility/electricityInstallation' })
  public stateElectricityInstallation!: ElectricityInstallation
  @State('consumingDevices', { namespace: 'eMobility/consumingDevices' })
  public stateConsumingDevices!: ConsumingDevice[]
  @State('requestId', { namespace: 'eMobility' })
  public requestId!: string

  public electricityInstallation: ElectricityInstallation = {
    meterType: null,
    electricityPowerSupplyType: null,
    power: null
  }

  public loading: boolean = false

  public isMeterTypeModalOpen: boolean = false
  //#endregion

  //#region [Getter]
  @Getter('getHasEan', { namespace: 'eMobility/identification' })
  public getHasEan!: string
  //#endregion

  //region [Mutation]
  @Mutation('STORE', { namespace: 'eMobility/electricityInstallation' })
  public store!: (value: ElectricityInstallation) => void
  @Mutation('STORE', { namespace: 'eMobility/consumingDevices' })
  public storeDevices!: (index: { consumingDevices: ConsumingDevice[] | null }) => void
  @Mutation('STORE_HAS_EAN', { namespace: 'eMobility/identification' })
  public storeHasEan!: (index: { hasEan: string | null }) => void
  //#endregion

  //region [Method]
  public mounted() {
    this.electricityInstallation = helper.clone(this.stateElectricityInstallation)
  }

  public async getConsumingDevice() {
    const response = await this.$api.get<ConsumingDevice[]>(
      `api/ElectricMobility/energy-consuming-devices`,
      {
        cache: {
          cachePredicate: (status) => true,
        }
      }
    )

    return response.data
  }

  public onMeterTypeModalClick(): void {
    this.$appInsights.trackEvent('EMobility_TooltipClick', {
      Segment: 'EMobility',
      WebRef: this.requestId,
      Tooltip: 'MeterType'
    })
    this.isMeterTypeModalOpen = !this.isMeterTypeModalOpen
  }

  public async save(next: boolean, isValid: boolean): Promise<boolean | void> {
    if (isValid && next) {
      if (!this.stateConsumingDevices) {
        this.loading = true
        const data = await this.getConsumingDevice()
        this.storeDevices({ consumingDevices: data })
        this.loading = false
      }

      this.$appInsights.trackEvent('EMobility_StepElectricInstallation_Validated', {
        Segment: 'EMobility',
        WebRef: this.requestId,
        IdentificationByEan: 'false'
      })
    }
    this.store(this.electricityInstallation)
  }
  //#endregion
}
</script>
